import {store} from '../store';
import {Feature, stopModalLoading} from '../reducers/environmentReducer';
import {getLocaleText} from '../util/translation';
import {startModalLoading} from '../reducers/environmentReducer';
import {DefaultCart} from '../reducers/cartReducer';
import {MerchantConfiguration} from '../reducers/merchantConfigurationReducer';
import {type IRecommendedProduct} from '../models/IRecommendedProducts';
import {addProductToCart, cartItemQuantity} from '../util/cart';
import {initQuantityChangerEvent} from './quantityChanger';
import {$qsAll, $qs} from '../../../@shared/ts/dom';
import {FeatureFlag} from '../../../@type/features';
import saleImageURL from '../../img/sale.svg';
import darkSpinnerImageURL from '../../img/spinner-dark.svg';
import plusImageURL from '../../img/plus.svg';
import minusImageURL from '../../img/minus.svg';
import {renderOrderNotice, requestCartCalculation} from '../payment/order';

export function initRelatedProducts() {
	if (!Feature.enabled(FeatureFlag.RECOMMENDED_PRODUCTS)) {
		return;
	}

	let previousCurrencyData = '';
	let previousCartData = '';

	store.subscribe(() => {
		const header = Feature.metadata<string>(FeatureFlag.RECOMMENDED_PRODUCTS, 'rp_header')
			? Feature.metadata<string>(FeatureFlag.RECOMMENDED_PRODUCTS, 'rp_header')
			: getLocaleText('Recommended for you');
		const recommendedProducts = Feature.dynamicMetadata<IRecommendedProduct[]>(FeatureFlag.RECOMMENDED_PRODUCTS, 'recommended_products');
		const cartData = JSON.stringify(DefaultCart.contents());
		const currencyData = JSON.stringify(MerchantConfiguration.currency.configuration());

		if (recommendedProducts && recommendedProducts.length > 0 && header) {
			if (cartData !== previousCartData || currencyData !== previousCurrencyData) {
				previousCartData = cartData;
				previousCurrencyData = currencyData;
				renderRecommendedProductsMiniSlider(recommendedProducts, header);
			}
		}
	});

	$qsAll('#pp-products-list-related', $el => {
		$el.addEventListener('scroll', () => {
			const $elName = $el.id ? '#' + $el.id : '.' + $el.className;
			fadeAdjuster(
				$elName,
				$el.scrollLeft === 0,
				Math.round($el.offsetWidth + $el.scrollLeft) >= $el.scrollWidth,
			);
		});
	});
}

/**
 * Function for rendering recommended product's mini slider in the checkout window
 * @param relatedProducts Recommended products data
 * @param title Recommended products title
 */
function renderRecommendedProductsMiniSlider(products: IRecommendedProduct[], title: string) {
	// For clearing the related product section after each refresh
	$qsAll('.pp-related-product-body', $el => {
		$el.remove();
	});
	$qs('.pp-rp-spacer')?.remove();
	$qs('#pp-related-products-section')?.classList.remove('hide');
	for (const element of $qsAll('.related-products-title')) {
		element.innerHTML = title;
		element.classList.remove('hide');
	}

	const relatedList = $qs('#pp-products-list-related-main');
	const relatedListMobile = $qs('.pp-products-list-related-mobile');
	for (const item of products) {
		const isBundleOrVariable = item.bundle || item.variable;
		const rpBody = document.createElement('div');
		rpBody.id = String(item.id);
		rpBody.className = 'pp-related-product-body';
		rpBody.innerHTML = `<div class="pp-rp-content">
								<img class="pp-related-product-img ${item.img_src ? '' : 'hide'}" src=${item.img_src as unknown as string}>
								<div class="flex col">
									<span class="pp-rp-title">${item.name}</span>
									<div class="flex">
										<img src="${saleImageURL}" class="${item.sale ? 'pp-rp-sale' : 'hide'}"></img>
										<span class="pp-rp-price${(item.sale && !isBundleOrVariable) ? ' pp-rp-price-sale' : (item.sale && isBundleOrVariable) ? ' pp-rp-bv-sale' : ''}">
											${isBundleOrVariable ? item.price.replace(' &ndash; ', '<span> - </span>') : item.price}
										</span>
									</div>
								</div>
							</div>`;

		if (item.variable) {
			rpBody.append(renderVariationFields(item));
		}

		const qtyChanger = renderQuantityChanger(item.id);

		if (qtyChanger) {
			rpBody.append(qtyChanger);
		} else {
			rpBody.innerHTML += `<div>
            ${item.bundle ? `<a href="${item.permalink}" class="pp-lp-btn" target="_parent">${getLocaleText('View options')}</a>`
		: `<button class="pp-lp-btn ${item.variable ? 'pp-js-display' : 'pp-js-add-btn'}" data-pid=${item.id}>
                        ${item.variable ? '' : '<span style="pointer-events: none;">+</span>'}
                        <span style="pointer-events: none;">${item.variable ? getLocaleText('Customize') : getLocaleText('Add')}</span>
                    </button>`}
            </div>`;
		}

		relatedList?.append(rpBody);
		relatedListMobile?.append(rpBody.cloneNode(true));
	}

	variationFieldsUI();

	initQuantityChangerEvent();

	for (const addBtn of $qsAll('.pp-js-add-btn')) {
		addBtn.addEventListener('click', async event => {
			const $target = event.target as HTMLInputElement;
			store.dispatch(startModalLoading());

			$target.disabled = true;
			const targetMarkup = $target.innerHTML;
			$target.innerHTML = `<img src="${darkSpinnerImageURL}" class="linked-product-spinner"/>`;

			const productId = $target.dataset['pid'];
			if (!productId || Number.isNaN(Number(productId))) {
				renderOrderNotice(getLocaleText('Something went wrong. Please refresh the page and try again.'));
			}

			await addProductToCart(Number(productId));

			$target.disabled = false;
			$target.innerHTML = targetMarkup;

			// Note:  Any adding to cart errors will be reported by the cart calculation request as a notice
			await requestCartCalculation();
			store.dispatch(stopModalLoading());
		});
	}
}

function fadeAdjuster(elementName: string, isAtLeftEnd: boolean, isAtRightEnd: boolean) {
	if (isAtLeftEnd) {
		$qs(`${elementName}+.pp-rp-fade-left`)?.classList.add('pp-rp-fade-left-hide');
	} else {
		$qs(`${elementName}+.pp-rp-fade-left`)?.classList.remove('pp-rp-fade-left-hide');
	}

	if (isAtRightEnd) {
		$qs(`${elementName}+.pp-rp-fade-left+.pp-rp-fade-right`)?.classList.add('pp-rp-fade-right-hide');
	} else {
		$qs(`${elementName}+.pp-rp-fade-left+.pp-rp-fade-right`)?.classList.remove('pp-rp-fade-right-hide');
	}
}

/**
 * Renders variation fields and buttons for variable product.
 * @param linkedItem Recommended product
 * @returns DOM element
 */
function renderVariationFields(item: IRecommendedProduct) {
	const formContainer = document.createElement('div');
	formContainer.setAttribute('data-pid', (item.id).toString());
	formContainer.classList.add('flex', 'col', 'hide', 'pp-lp-form-container');

	const variationForm = document.createElement('form');
	variationForm.setAttribute('data-pid', (item.id).toString());
	variationForm.className = 'pp-variation-form';
	for (const attr of item.attributes) {
		const container = document.createElement('div');
		container.className = 'pp-variation-select-field';
		const label = document.createElement('label');
		label.setAttribute('for', attr.name);
		label.innerHTML = attr.label;
		const select = document.createElement('select');
		select.name = 'attribute_' + attr.name;
		select.setAttribute('data-attribute_name', 'attribute_' + attr.name);
		for (const option of attr.options) {
			const opt = document.createElement('option');
			opt.value = option;
			opt.text = option.charAt(0).toUpperCase() + option.slice(1);
			select.add(opt, null);
		}

		container.append(label);
		container.append(select);
		variationForm.append(container);
	}

	const addToCartButton = document.createElement('button');
	addToCartButton.classList.add('pp-lp-btn', 'pp-variable-add-btn');
	addToCartButton.setAttribute('data-pid', (item.id).toString());
	addToCartButton.innerHTML = `<span style="pointer-events: none;">+</span><span style="pointer-events: none;">${getLocaleText('ADD')}</span>`;

	const cancelButton = document.createElement('button');
	cancelButton.classList.add('pp-variation-cancel-btn', 'pp-js-cancel-btn');
	cancelButton.setAttribute('data-pid', (item.id).toString());
	cancelButton.innerText = getLocaleText('Close');

	formContainer.append(variationForm);
	formContainer.append(addToCartButton);
	formContainer.append(cancelButton);

	return formContainer;
}

/**
 * Check if linked item is in cart, then render the quantity changer button to replace the add button.
 * @param linkedID Linked product's ID
 * @param cart Cart items
 * @returns DOM element
 */
function renderQuantityChanger(id: number) {
	const cart = DefaultCart.contents();
	for (let i = cart.length - 1; i >= 0; i--) {
		const item = cart[i];
		if (item && id === item.product_id) {
			const $div = document.createElement('div');
			$div.innerHTML += `
				<div class="pp-quantity-changer" style="justify-content: center;">
					<button type="button" class="flex-center decrease-qty qty-btn ${cartItemQuantity(item) <= 1 ? 'scroll-end' : ''}" data-qid="${item
	.item_key ?? ''}"><img src="${minusImageURL}" /></button>
					<input style="color: black;" type="number" min="0" max="${item.stock_qty ? item.stock_qty : ''}" class="qty-fs" value="${cartItemQuantity(item)}" data-qid="${item.item_key ?? ''}" required/>
					<button type="button" class="flex-center increase-qty qty-btn ${(item.stock_qty && cartItemQuantity(item) >= item.stock_qty) ? 'scroll-end' : ''}" data-qid="${item
	.item_key ?? ''}"><img src="${plusImageURL}" /></button>
				</div>`;
			return $div;
		}
	}

	return '';
}

/**
 * For handling UI for variable product.
 */
function variationFieldsUI() {
	// For opening the variation form
	for (const button of $qsAll('.pp-js-display')) {
		button.addEventListener('click', event => {
			const id = (event.target as HTMLElement).dataset['pid'];
			const container = $qsAll('.pp-lp-form-container[data-pid=\'' + id + '\']');
			container?.forEach(element => {
				element.classList.remove('hide');
			});
			$qsAll('.pp-js-display[data-pid=\'' + id + '\']', element => {
				element?.classList.add('hide');
			});
		});
	}

	// For closing the variation form
	for (const button of $qsAll('.pp-js-cancel-btn')) {
		button.addEventListener('click', event => {
			const id = (event.target as HTMLElement).dataset['pid'];
			const container = $qsAll('.pp-lp-form-container[data-pid=\'' + id + '\']');
			container?.forEach(element => {
				element.classList.add('hide');
			});
			$qsAll('.pp-js-display[data-pid=\'' + id + '\']', element => {
				element?.classList.remove('hide');
			});
		});
	}

	// For adding variation product to cart
	for (const variationBtn of $qsAll('.pp-variable-add-btn')) {
		variationBtn.addEventListener('click', async event => {
			const $target = event.target as HTMLInputElement;
			const productId = Number($target.dataset['pid']);
			const recommendedProducts = Feature.dynamicMetadata<IRecommendedProduct[]>(FeatureFlag.RECOMMENDED_PRODUCTS, 'recommended_products');

			if (!productId || Number.isNaN(productId) || !recommendedProducts || recommendedProducts.length === 0) {
				renderOrderNotice(getLocaleText('Something went wrong. Please refresh the page and try again.'));
				return;
			}

			store.dispatch(startModalLoading());
			$target.disabled = true;
			const targetMarkup = $target.innerHTML;
			$target.innerHTML = `<img src="${darkSpinnerImageURL}" class="linked-product-spinner"/>`;

			const variationForm = $qs<HTMLFormElement>('.pp-variation-form[data-pid=\'' + productId + '\']');
			const variationAttributes = Array.from((variationForm?.elements ?? []) as HTMLInputElement[])
				.map(input => [input.name, input.value] satisfies [name:string, value:string ]);

			const variationId = recommendedProducts
				.find(product => product.id === productId)?.variations
				.find(variation => {
					for (const [name, value] of variationAttributes) {
						if (variation.attributes[name] !== value) {
							return false;
						}
					}

					return true;
				})
				?.variation_id;

			await addProductToCart(productId, 1, {variationId, variationAttributes});

			$target.disabled = false;
			$target.innerHTML = targetMarkup;

			// Note:  Any adding to cart errors will be reported by the cart calculation request as a notice
			await requestCartCalculation();
			store.dispatch(stopModalLoading());
		});
	}
}
